// src/utils/emailService.js - Email notification service for Norns' Weave

const CLOUD_FUNCTION_URL = process.env.NODE_ENV === 'production'
  ? 'https://us-central1-heimdl.cloudfunctions.net/sendBookingEmail'
  : 'http://localhost:5001/heimdl/us-central1/sendBookingEmail';

/**
 * Send a booking notification email
 * @param {Object} params - Email parameters
 * @param {string} params.type - Email type: 'request_submitted', 'approved', 'declined', 'reminder', 'cancellation', 'new_request_librarian'
 * @param {string} params.to - Recipient email address
 * @param {string} params.teacherName - Teacher's name
 * @param {string} params.bookingDate - Booking date (YYYY-MM-DD)
 * @param {string} params.bookingTime - Booking time (e.g., "10:00 - 11:00")
 * @param {string} params.library - Library type ('elementary' or 'middle')
 * @param {string} [params.grade] - Grade/class (optional)
 * @param {string} [params.librarianName] - Librarian name (optional)
 * @param {string} [params.schoolName] - School name (optional)
 * @param {string} [params.magicLinkUrl] - Magic link URL (optional)
 * @param {string} [params.rejectionReason] - Reason for declining (for declined emails)
 * @param {string} [params.suggestedAlternatives] - Alternative times (for declined emails)
 * @param {string} [params.replyTo] - Reply-to email address
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export async function sendBookingEmail(params) {
  try {
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Email sending failed:', result);
      return {
        success: false,
        error: result.error || 'Failed to send email',
      };
    }

    return {
      success: true,
      messageId: result.messageId,
    };
  } catch (error) {
    console.error('Email service error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Send email when a booking request is submitted
 */
export async function sendBookingRequestEmail(booking, settings) {
  const baseUrl = window.location.origin;
  const magicLinkUrl = booking.magicLinkToken
    ? `${baseUrl}/calendar/booking/${booking.magicLinkToken}`
    : null;

  // Send to teacher (reply-to is librarian's email so teachers can reply directly)
  const teacherResult = await sendBookingEmail({
    type: 'request_submitted',
    to: booking.teacherEmail,
    teacherName: booking.teacherName,
    bookingDate: booking.date,
    bookingTime: `${booking.startTime} - ${booking.endTime}`,
    library: booking.library,
    grade: booking.grade,
    librarianName: settings?.yourName,
    schoolName: settings?.schoolName,
    magicLinkUrl,
    replyTo: settings?.librarianEmail,
  });

  // Send to librarian if email is configured (reply-to is teacher's email)
  if (settings?.emailEnabled && settings?.librarianEmail) {
    await sendBookingEmail({
      type: 'new_request_librarian',
      to: settings.librarianEmail,
      teacherName: booking.teacherName,
      bookingDate: booking.date,
      bookingTime: `${booking.startTime} - ${booking.endTime}`,
      library: booking.library,
      grade: booking.grade,
      schoolName: settings?.schoolName,
      magicLinkUrl: `${baseUrl}/admin/norns-weave`,
      replyTo: booking.teacherEmail,
    });
  }

  return teacherResult;
}

/**
 * Send email when a booking is approved
 */
export async function sendBookingApprovedEmail(booking, settings) {
  const baseUrl = window.location.origin;
  const magicLinkUrl = booking.magicLinkToken
    ? `${baseUrl}/calendar/booking/${booking.magicLinkToken}`
    : null;

  return sendBookingEmail({
    type: 'approved',
    to: booking.teacherEmail,
    teacherName: booking.teacherName,
    bookingDate: booking.date,
    bookingTime: `${booking.startTime} - ${booking.endTime}`,
    library: booking.library,
    grade: booking.grade,
    librarianName: settings?.yourName,
    schoolName: settings?.schoolName,
    magicLinkUrl,
    replyTo: settings?.librarianEmail,
  });
}

/**
 * Send email when a booking is declined
 */
export async function sendBookingDeclinedEmail(booking, settings, rejectionReason, suggestedAlternatives) {
  return sendBookingEmail({
    type: 'declined',
    to: booking.teacherEmail,
    teacherName: booking.teacherName,
    bookingDate: booking.date,
    bookingTime: `${booking.startTime} - ${booking.endTime}`,
    library: booking.library,
    grade: booking.grade,
    librarianName: settings?.yourName,
    schoolName: settings?.schoolName,
    rejectionReason,
    suggestedAlternatives,
    replyTo: settings?.librarianEmail,
  });
}

/**
 * Send reminder email for upcoming booking
 */
export async function sendBookingReminderEmail(booking, settings) {
  const baseUrl = window.location.origin;
  const magicLinkUrl = booking.magicLinkToken
    ? `${baseUrl}/calendar/booking/${booking.magicLinkToken}`
    : null;

  return sendBookingEmail({
    type: 'reminder',
    to: booking.teacherEmail,
    teacherName: booking.teacherName,
    bookingDate: booking.date,
    bookingTime: `${booking.startTime} - ${booking.endTime}`,
    library: booking.library,
    grade: booking.grade,
    librarianName: settings?.yourName,
    schoolName: settings?.schoolName,
    magicLinkUrl,
    replyTo: settings?.librarianEmail,
  });
}

/**
 * Send email when a booking is cancelled
 */
export async function sendBookingCancellationEmail(booking, settings) {
  return sendBookingEmail({
    type: 'cancellation',
    to: booking.teacherEmail,
    teacherName: booking.teacherName,
    bookingDate: booking.date,
    bookingTime: `${booking.startTime} - ${booking.endTime}`,
    library: booking.library,
    grade: booking.grade,
    librarianName: settings?.yourName,
    schoolName: settings?.schoolName,
    cancellationReason: booking.cancellationReason,
    replyTo: settings?.librarianEmail,
  });
}

/**
 * Send notification for upcoming holy day (mass shift)
 * Used to remind teachers about class cancellation/skip
 */
export async function sendHolyDayNotificationEmail(params) {
  return sendBookingEmail({
    type: 'holy_day_notification',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    holyDayName: params.holyDayName,
    holyDayDate: params.holyDayDate,
    massTime: params.massTime,
    affectedClass: params.affectedClass,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send notification for upcoming adoration day
 */
export async function sendAdorationNotificationEmail(params) {
  return sendBookingEmail({
    type: 'adoration_notification',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    adorationDate: params.adorationDate,
    massTime: params.massTime,
    affectedClass: params.affectedClass,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send notification for upcoming holiday/closure
 */
export async function sendHolidayNotificationEmail(params) {
  return sendBookingEmail({
    type: 'holiday_notification',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    holidayName: params.holidayName,
    holidayDate: params.holidayDate,
    endDate: params.endDate,
    affectedClass: params.affectedClass,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send notification for upcoming half day
 */
export async function sendHalfDayNotificationEmail(params) {
  return sendBookingEmail({
    type: 'half_day_notification',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    eventName: params.eventName,
    eventDate: params.eventDate,
    halfDayPeriod: params.halfDayPeriod,
    affectedClass: params.affectedClass,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send reminder for recurring/fixed class that will be skipped
 * This is the 1-week reminder mentioned by user for skipped recurring classes
 */
export async function sendFixedClassSkipReminderEmail(params) {
  return sendBookingEmail({
    type: 'fixed_class_skip_reminder',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    skipDate: params.skipDate,
    skipReason: params.skipReason,
    classInfo: params.classInfo,
    nextOccurrence: params.nextOccurrence,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send notification when a schedule override cancels fixed classes
 * @param {Object} params - Email parameters
 * @param {string} params.teacherEmail - Teacher's email
 * @param {string} params.teacherName - Teacher's name
 * @param {string} params.overrideDescription - Description of the override (e.g., "State Testing")
 * @param {string} params.startDate - Override start date
 * @param {string} params.endDate - Override end date (optional, same as start if single day)
 * @param {Array} params.affectedDates - List of dates when their class is affected
 * @param {string} params.classInfo - Class info (e.g., "3rd Grade - Mondays 10:00 AM")
 * @param {string} params.librarianName - Librarian's name
 * @param {string} params.schoolName - School name
 * @param {string} params.librarianEmail - Librarian's email (for reply-to)
 */
export async function sendOverrideCancellationEmail(params) {
  return sendBookingEmail({
    type: 'override_cancellation',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    overrideDescription: params.overrideDescription,
    startDate: params.startDate,
    endDate: params.endDate,
    affectedDates: params.affectedDates,
    classInfo: params.classInfo,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send closure notification to an aide
 */
export async function sendAideClosureEmail(params) {
  return sendBookingEmail({
    type: 'closure_aide_notification',
    to: params.aideEmail,
    aideName: params.aideName,
    dayType: params.dayType,
    closureDate: params.closureDate,
    closureReason: params.closureReason,
    aideWorkAssignment: params.aideWorkAssignment,
    aideReportTo: params.aideReportTo,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send closure notification to library assistant (full-day closures only)
 */
export async function sendLibraryAssistantClosureEmail(params) {
  return sendBookingEmail({
    type: 'closure_library_assistant',
    to: params.assistantEmail,
    assistantName: params.assistantName,
    closureDate: params.closureDate,
    closureDateEnd: params.closureDateEnd,
    closureReason: params.closureReason,
    library: params.library,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send emergency closure notification to library chairs
 */
export async function sendLibraryChairsClosureEmail(params) {
  return sendBookingEmail({
    type: 'closure_library_chairs',
    to: params.chairEmail,
    chairName: params.chairName,
    closureDate: params.closureDate,
    closureReason: params.closureReason,
    library: params.library,
    affectedClassCount: params.affectedClassCount,
    customMessage: params.customMessage,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send recess cancellation notification to teachers
 */
export async function sendRecessCancellationEmail(params) {
  return sendBookingEmail({
    type: 'recess_cancellation',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    closureDate: params.closureDate,
    closureReason: params.closureReason,
    recessStartTime: params.recessStartTime,
    recessEndTime: params.recessEndTime,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send closure CC notification to admin contacts
 */
export async function sendAdminClosureCCEmail(params) {
  return sendBookingEmail({
    type: 'closure_admin_cc',
    to: params.contactEmail,
    contactName: params.contactName,
    closureDate: params.closureDate,
    closureDateEnd: params.closureDateEnd,
    closureReason: params.closureReason,
    library: params.library,
    timeType: params.timeType,
    startTime: params.startTime,
    endTime: params.endTime,
    affectedClassCount: params.affectedClassCount,
    customMessage: params.customMessage,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send late cancellation notification to library chairs
 */
export async function sendTeacherLateCancellationEmail(params) {
  return sendBookingEmail({
    type: 'teacher_late_cancellation',
    to: params.chairEmail,
    grade: params.grade,
    subject: params.subject,
    teacherName: params.teacherName,
    classDate: params.classDate,
    classTime: params.classTime,
    library: params.library,
    daysNotice: params.daysNotice,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send Book Fair announcement to all teachers
 */
export async function sendBookFairAnnouncementEmail(params) {
  return sendBookingEmail({
    type: 'book_fair_announcement',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    startDate: params.startDate,
    endDate: params.endDate,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send weekly digest to library chairs
 */
export async function sendWeeklyDigestEmail(params) {
  return sendBookingEmail({
    type: 'weekly_digest',
    to: params.chairEmail,
    chairName: params.chairName,
    weekStart: params.weekStart,
    elementarySchedule: params.elementarySchedule,
    middleSchedule: params.middleSchedule,
    notes: params.notes,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send single-day holiday notification
 */
export async function sendHolidaySingleDayEmail(params) {
  return sendBookingEmail({
    type: 'holiday_single_day',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    holidayName: params.holidayName,
    holidayDate: params.holidayDate,
    affectedClass: params.affectedClass,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}

/**
 * Send multi-day holiday break notification
 */
export async function sendHolidayBreakEmail(params) {
  return sendBookingEmail({
    type: 'holiday_break',
    to: params.teacherEmail,
    teacherName: params.teacherName,
    holidayName: params.holidayName,
    startDate: params.startDate,
    endDate: params.endDate,
    returnDate: params.returnDate,
    librarianName: params.librarianName,
    schoolName: params.schoolName,
    replyTo: params.librarianEmail,
  });
}
